import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { usersApi } from '../../lib/api';
import { useAuthStore } from '../../lib/store';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

function acceptUpdatedUser(user, onSaved) {
  const auth = useAuthStore.getState();
  if (auth.user?.id === user.id) auth.setUser(user);
  onSaved?.(user);
}

export function EmployeePhotoUpload({ user, onSaved, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      toast.error('Bitte PNG, JPEG oder WebP mit maximal 5 MB auswählen.');
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const result = await usersApi.uploadAvatar(user.id, data);
      acceptUpdatedUser(result.data, onSaved);
      toast.success('Mitarbeiterbild gespeichert');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Foto konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label htmlFor={`employee-photo-${user.id}`}>Mitarbeiterbild</Label>
      <Input id={`employee-photo-${user.id}`} type="file" accept="image/png,image/jpeg,image/webp" disabled={busy || disabled}
        onChange={(event) => { upload(event.target.files?.[0]); event.target.value = ''; }} />
      <p className="text-xs text-muted-foreground">{busy ? 'Foto wird hochgeladen…' : disabled ? 'Bitte die Bearbeitung zuerst speichern oder abbrechen.' : 'PNG, JPEG oder WebP, maximal 5 MB. Das Foto wird direkt gespeichert.'}</p>
    </div>
  );
}

export default function PersonalDetailsEditor({ user, onSaved, onCancel }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { id, vorname, nachname, email, telefon } = user;
  useEffect(() => {
    setForm({ vorname: vorname || '', nachname: nachname || '', email: email || '', telefon: telefon || '' });
  }, [id, vorname, nachname, email, telefon]);
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await usersApi.update(user.id, { ...form, email: form.email || null, telefon: form.telefon || null });
      acceptUpdatedUser(result.data, onSaved);
      toast.success('Persönliche Angaben gespeichert');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Angaben konnten nicht gespeichert werden. Bitte Eingaben prüfen.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {Object.entries({ vorname: 'Vorname', nachname: 'Nachname', email: 'E-Mail', telefon: 'Telefon' }).map(([key, label]) => (
          <div key={key} className="space-y-2">
            <Label htmlFor={`personal-${key}`}>{label}</Label>
            <Input id={`personal-${key}`} type={key === 'email' ? 'email' : key === 'telefon' ? 'tel' : 'text'}
              required={key === 'vorname' || key === 'nachname'} maxLength={key === 'telefon' ? 30 : key === 'email' ? 254 : 100}
              value={form[key] || ''} onChange={event => setForm(old => ({ ...old, [key]: event.target.value }))} />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Abbrechen</Button>}
        <Button type="submit" disabled={busy}>{busy ? 'Speichere…' : 'Angaben speichern'}</Button>
      </div>
    </form>
  );
}
